import { useState } from "react";
import { useQuery } from "react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/context/auth.context";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CreditCard, DollarSign, TrendingUp, Calendar, AlertCircle, AlertTriangle, CheckCircle2, Clock, ArrowLeft } from "lucide-react";
import { motion } from "framer-motion";
import DataTable from "@/components/datatable/Datatable";
import { ColumnDef } from "@tanstack/react-table";
import DataTableColumnHeader from "@/components/datatable/DataTableColumnHeader";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { Progress } from "@/components/ui/progress";

export default function PaymentHistory() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Filter and search state
  const [searchText, setSearchText] = useState("");
  const [columnFilters, setColumnFilters] = useState([]);
  const [sorting, setSorting] = useState([
    {
      id: "paymentDate",
      desc: true,
    },
  ]);

  // Fetch all loans for the member to get payments
  const { data: loansData, isLoading: loansLoading } = useQuery(
    ["my-loans", user?.id],
    async () => {
      if (!user?.id) {
        return { loans: [] };
      }

      try {
        const { data } = await api.get("/loans/my");
        const responseData = data.data || {};
        const loans = Array.isArray(responseData.loans) ? responseData.loans : Array.isArray(responseData) ? responseData : [];
        return { loans };
      } catch (err) {
        console.error("Error fetching loans:", err);
        throw err;
      }
    },
    {
      enabled: !!user?.id,
      refetchOnMount: true,
      refetchOnWindowFocus: true,
    }
  );

  // Fetch payments for each loan
  const { data: allPayments, isLoading: paymentsLoading } = useQuery(
    ["all-loan-payments", loansData?.loans],
    async () => {
      if (!loansData?.loans || loansData.loans.length === 0) {
        return [];
      }

      try {
        // Fetch payments for each loan
        const paymentPromises = loansData.loans.map(async (loan: any) => {
          try {
            const { data } = await api.get(`/loans/${loan.id}/payments`);
            const payments = Array.isArray(data.data) ? data.data : [];
            // Add loan information to each payment
            return payments.map((payment: any) => ({
              ...payment,
              loanId: loan.id,
              loanAmount: loan.amount,
              loanStatus: loan.status,
              loanPurpose: loan.purpose,
              loanApplicationDate: loan.applicationDate,
            }));
          } catch (err) {
            console.error(`Error fetching payments for loan ${loan.id}:`, err);
            return [];
          }
        });

        const paymentArrays = await Promise.all(paymentPromises);
        // Flatten the array of arrays
        return paymentArrays.flat();
      } catch (err) {
        console.error("Error fetching payments:", err);
        return [];
      }
    },
    {
      enabled: !!loansData?.loans && loansData.loans.length > 0,
    }
  );

  const payments = allPayments || [];
  const isLoading = loansLoading || paymentsLoading;

  // Calculate summary statistics
  const totalPaid = payments.reduce((sum: number, p: any) => sum + (p.amount || 0), 0);
  const totalPrincipal = payments.reduce((sum: number, p: any) => sum + (p.principalAmount || 0), 0);
  const totalInterest = payments.reduce((sum: number, p: any) => sum + (p.interestAmount || 0), 0);
  const totalPenalties = payments.reduce((sum: number, p: any) => sum + (p.penaltyAmount || 0), 0);
  const verifiedPayments = payments.filter((p: any) => p.isVerified).length;
  const latePayments = payments.filter((p: any) => p.isLate).length;

  const columns: ColumnDef<any>[] = [
    {
      accessorKey: "paymentDate",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Payment Date" />
      ),
      cell: ({ row }) => {
        const date = row.getValue("paymentDate");
        return date ? format(new Date(date as string), "MMM dd, yyyy") : "N/A";
      },
    },
    {
      accessorKey: "loanId",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Loan ID" />
      ),
      cell: ({ row }) => {
        const loanId = row.getValue("loanId") as number;
        const loanStatus = row.original.loanStatus;
        return (
          <div className="flex items-center gap-2">
            <span className="font-medium">#{loanId}</span>
            <Badge
              variant={
                loanStatus === "Active"
                  ? "default"
                  : loanStatus === "Completed"
                  ? "default"
                  : loanStatus === "Approved"
                  ? "default"
                  : "secondary"
              }
              className={
                loanStatus === "Active"
                  ? "bg-green-500"
                  : loanStatus === "Completed"
                  ? "bg-gray-500"
                  : loanStatus === "Approved"
                  ? "bg-blue-500"
                  : ""
              }
            >
              {loanStatus}
            </Badge>
          </div>
        );
      },
    },
    {
      accessorKey: "amount",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Amount Paid" />
      ),
      filterFn: (row, _id, value) => {
        if (!value || !Array.isArray(value) || value.length === 0) return true;
        const amountValue = row.original.amount;
        const amount = typeof amountValue === "string" ? parseFloat(amountValue) : (amountValue as number) || 0;
        return value.some((v) => {
          if (v === "very_low") return amount >= 0 && amount < 10000;
          if (v === "low") return amount >= 10000 && amount < 50000;
          if (v === "medium") return amount >= 50000 && amount < 100000;
          if (v === "high") return amount >= 100000 && amount < 200000;
          if (v === "very_high") return amount >= 200000;
          return false;
        });
      },
      cell: ({ row }) => {
        const amountValue = row.getValue("amount");
        const amount = typeof amountValue === "string" ? parseFloat(amountValue) : (amountValue as number) || 0;
        return <span className="font-semibold text-green-600">{Math.round(amount).toLocaleString()} FRW</span>;
      },
    },
    {
      accessorKey: "principalAmount",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Principal" />
      ),
      cell: ({ row }) => {
        const principal = row.getValue("principalAmount") as number || 0;
        return <span>{Math.round(principal).toLocaleString()} FRW</span>;
      },
    },
    {
      accessorKey: "interestAmount",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Interest" />
      ),
      cell: ({ row }) => {
        const interest = row.getValue("interestAmount") as number || 0;
        return <span>{Math.round(interest).toLocaleString()} FRW</span>;
      },
    },
    {
      accessorKey: "penaltyAmount",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Penalty" />
      ),
      cell: ({ row }) => {
        const penalty = row.getValue("penaltyAmount") as number || 0;
        const isLate = row.original.isLate;
        const daysLate = row.original.daysLate;
        
        if (penalty > 0) {
          return (
            <div className="flex items-center gap-1">
              <AlertTriangle className="h-3 w-3 text-amber-600" />
              <span className="text-amber-600 font-medium">
                {Math.round(penalty).toLocaleString()} FRW
              </span>
              {isLate && daysLate && (
                <Badge variant="outline" className="text-xs">
                  {daysLate} days late
                </Badge>
              )}
            </div>
          );
        }
        return <span className="text-muted-foreground">-</span>;
      },
    },
    {
      accessorKey: "paymentMethod",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Payment Method" />
      ),
      cell: ({ row }) => {
        const method = row.getValue("paymentMethod") as string;
        return <span>{method || "N/A"}</span>;
      },
    },
    {
      accessorKey: "isVerified",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Status" />
      ),
      cell: ({ row }) => {
        const isVerified = row.getValue("isVerified") as boolean;
        return isVerified ? (
          <Badge variant="default" className="bg-green-500">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Verified
          </Badge>
        ) : (
          <Badge variant="secondary">
            <Clock className="h-3 w-3 mr-1" />
            Pending
          </Badge>
        );
      },
    },
  ];

  // Filter options
  const paymentMethodOptions = [
    { label: "Cash", value: "Cash" },
    { label: "Bank Transfer", value: "Bank Transfer" },
    { label: "Mobile Money", value: "Mobile Money" },
    { label: "Check", value: "Check" },
    { label: "Card", value: "Card" },
  ];

  const statusOptions = [
    { label: "Verified", value: "true" },
    { label: "Pending", value: "false" },
  ];

  const amountRangeOptions = [
    { label: "0 - 10,000 FRW", value: "very_low" },
    { label: "10,000 - 50,000 FRW", value: "low" },
    { label: "50,000 - 100,000 FRW", value: "medium" },
    { label: "100,000 - 200,000 FRW", value: "high" },
    { label: "200,000+ FRW", value: "very_high" },
  ];

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/member/loans")}
            className="h-8 w-8"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-theme-text">Payment History</h1>
            <p className="text-xs sm:text-sm text-muted-foreground mt-1">
              View all your loan payment records
            </p>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
        {/* Total Paid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <Card className="group relative overflow-hidden border-2 border-[#4dbd07] bg-white shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 hover:border-[#3d9a06]">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300">
                Total Paid
              </CardTitle>
              <DollarSign className="h-4 w-4 sm:h-5 sm:w-5 text-[#4dbd07] group-hover:scale-110 transition-transform duration-300" />
            </CardHeader>
            <CardContent>
              <div className="flex items-baseline gap-2 mb-2">
                <div className="text-2xl sm:text-3xl font-bold text-[#4dbd07]">
                  {totalPaid.toLocaleString()}
                </div>
                <span className="text-sm font-semibold text-[#4dbd07]">FRW</span>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Total Payments */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
        >
          <Card className="group relative overflow-hidden border-2 border-[#4dbd07] bg-white shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 hover:border-[#3d9a06]">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300">
                Total Payments
              </CardTitle>
              <CreditCard className="h-4 w-4 sm:h-5 sm:w-5 text-[#4dbd07] group-hover:scale-110 transition-transform duration-300" />
            </CardHeader>
            <CardContent>
              <div className="flex items-baseline gap-2 mb-2">
                <div className="text-2xl sm:text-3xl font-bold text-[#4dbd07]">
                  {payments.length}
                </div>
                <span className="text-sm font-semibold text-[#4dbd07]">payments</span>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Verified Payments */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.3 }}
        >
          <Card className="group relative overflow-hidden border-2 border-[#4dbd07] bg-white shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 hover:border-[#3d9a06]">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300">
                Verified Payments
              </CardTitle>
              <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5 text-[#4dbd07] group-hover:scale-110 transition-transform duration-300" />
            </CardHeader>
            <CardContent>
              <div className="flex items-baseline gap-2 mb-2">
                <div className="text-2xl sm:text-3xl font-bold text-[#4dbd07]">
                  {verifiedPayments}
                </div>
                <span className="text-sm font-semibold text-[#4dbd07]">verified</span>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Late Payments */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.4 }}
        >
          <Card className="group relative overflow-hidden border-2 border-[#4dbd07] bg-white shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 hover:border-[#3d9a06]">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300">
                Late Payments
              </CardTitle>
              <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5 text-[#4dbd07] group-hover:scale-110 transition-transform duration-300" />
            </CardHeader>
            <CardContent>
              <div className="flex items-baseline gap-2 mb-2">
                <div className="text-2xl sm:text-3xl font-bold text-[#4dbd07]">
                  {latePayments}
                </div>
                <span className="text-sm font-semibold text-[#4dbd07]">late</span>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Payment Breakdown */}
      {(totalPrincipal > 0 || totalInterest > 0 || totalPenalties > 0) && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base sm:text-lg">Payment Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <p className="text-xs sm:text-sm text-muted-foreground">Principal</p>
                <p className="text-lg font-semibold">{totalPrincipal.toLocaleString()} FRW</p>
              </div>
              <div>
                <p className="text-xs sm:text-sm text-muted-foreground">Interest</p>
                <p className="text-lg font-semibold">{totalInterest.toLocaleString()} FRW</p>
              </div>
              <div>
                <p className="text-xs sm:text-sm text-muted-foreground">Penalties</p>
                <p className="text-lg font-semibold text-amber-600">
                  {totalPenalties.toLocaleString()} FRW
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Payments Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base sm:text-lg">All Payments</CardTitle>
          <CardDescription className="text-xs sm:text-sm">
            Complete history of all loan payments
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading payment history...</div>
          ) : payments.length > 0 ? (
            <DataTable
              title="Payment History"
              columns={columns}
              data={payments}
              isLoading={isLoading}
              defaultColumnVisibility={{}}
              facets={[
                {
                  name: "paymentMethod",
                  title: "Payment Method",
                  type: "select",
                  options: paymentMethodOptions,
                },
                {
                  name: "isVerified",
                  title: "Status",
                  type: "select",
                  options: statusOptions,
                },
                {
                  name: "amount",
                  title: "Amount Range",
                  type: "select",
                  options: amountRangeOptions,
                },
              ]}
              onSearch={(value) => setSearchText(value)}
              columnFilters={columnFilters}
              setColumnFilters={setColumnFilters}
              setSorting={setSorting}
              sorting={sorting}
              setPagination={() => {}}
              pageIndex={0}
              pageSize={25}
              pageCount={1}
              hidePagination={true}
              enableExport={false}
            />
          ) : (
            <div className="text-center py-8 sm:py-12">
              <CreditCard className="h-8 w-8 sm:h-12 sm:w-12 text-muted-foreground mx-auto mb-3 sm:mb-4" />
              <p className="text-base sm:text-lg font-semibold">No Payments Yet</p>
              <p className="text-xs sm:text-sm text-muted-foreground mt-2 px-4">
                You haven't made any loan payments yet. Payments will appear here once recorded.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

